/**
 * NavigationPanel - Prev/next/skip buttons and progress indicator
 *
 * Provides navigation controls for moving through feedback items
 * Requirement: ES-REQ-016, ES-REQ-086
 */

import React from 'react';
import styled from 'styled-components';
import { useLabeling } from '@/context/labelingContext';
import { ProgressIndicator } from './ProgressIndicator';

const Container = styled.div`
  background-color: var(--bg-card);
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const NavButton = styled.button`
  padding: 12px 16px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: var(--bg-card);
  color: var(--text-dark);
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--bg-panel);
    border-color: var(--accent-feedback);
  }

  &:active:not(:disabled) {
    background-color: #e0e0e0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const CompletionMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  background-color: var(--bg-success);
  border-radius: 8px;
  margin-top: 20px;
`;

const CompletionTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #2e7d32;
`;

const CompletionText = styled.p`
  margin: 0;
  font-size: var(--font-size-base);
  color: #558b2f;
`;

export const NavigationPanel: React.FC = () => {
  const { state, previousFeedback, nextFeedback, skipFeedback } = useLabeling();
  const { feedback, currentIndex } = state;

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === feedback.length - 1;
  const hasItems = feedback.length > 0;

  // Show completion message when all items are labeled
  if (feedback.length === 0 && state.isAuthenticated) {
    return (
      <Container>
        <CompletionMessage>
          <CompletionTitle>All Done!</CompletionTitle>
          <CompletionText>
            You've labeled all available feedback items.
            Great work!
          </CompletionText>
        </CompletionMessage>
      </Container>
    );
  }

  if (!hasItems) {
    return null;
  }

  return (
    <Container>
      <ButtonGroup>
        <NavButton
          onClick={previousFeedback}
          disabled={isFirst || state.isLoading}
          title="Previous feedback item"
        >
          ← Previous
        </NavButton>

        <NavButton
          onClick={skipFeedback}
          disabled={isLast || state.isLoading}
          title="Skip this item for now"
        >
          Skip
        </NavButton>

        <NavButton
          onClick={nextFeedback}
          disabled={isLast || state.isLoading}
          title="Next feedback item"
        >
          Next →
        </NavButton>
      </ButtonGroup>

      <ProgressIndicator />
    </Container>
  );
};

export default NavigationPanel;
